import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { isNil } from "lodash";
import { useFragment } from "react-relay";

import { formatSliderTimeLabel } from "@/components/AgendaFilters/utils/getSliderOptions";
import { useDepartmentGolfFacilitiesStartEndTime } from "@/features/Department";
import { HoleCount } from "@whoosh/design-system/src/components/icons_v2/courseLayout";
import { getNounOverride } from "@whoosh/utilities/src/FormatNounOverride/getNounOverride";

import { courseAvailabilityQueryInputAtom } from "../atoms/courseAvailabilityQueryInputs";
import { courseFilterAtom } from "../atoms/courseFilterAtom";
import { useGetDepartmentAvailabilityFilterBarValues_department$key } from "./__generated__/useGetDepartmentAvailabilityFilterBarValues_department.graphql";

export const useGetDepartmentAvailabilityFilterBarValues = (
  departmentRef: useGetDepartmentAvailabilityFilterBarValues_department$key | null
) => {
  const department = useFragment(
    useGetDepartmentAvailabilityFilterBarValuesFragmentString,
    departmentRef
  );

  const courseFilter = useAtomValue(courseFilterAtom);

  const courseAvailabilityQueryInput = useAtomValue(
    courseAvailabilityQueryInputAtom
  );

  const departmentStartEndTime = useDepartmentGolfFacilitiesStartEndTime(
    department ?? null
  );
  const golfFacilities =
    department?.facilities?.edges?.flatMap((edge) =>
      edge?.node && edge.node.type === "GOLF_COURSE" ? [edge.node] : []
    ) ?? [];
  const courseOverride = golfFacilities[0]?.copyOverrides?.course;
  const courseNoun = {
    singular: getNounOverride(courseOverride?.singular, t`Course`),
    plural: getNounOverride(courseOverride?.plural, t`Courses`),
  };
  const courseCountNoun = {
    singular: getNounOverride(courseOverride?.singular, t`course`, true),
    plural: getNounOverride(courseOverride?.plural, t`courses`, true),
  };

  const agendaFilterBarValues = [
    {
      value: courseDisplay(courseFilter, courseCountNoun),
      placeholder: courseNoun.plural,
    },
    {
      value: groupSizeDisplay(courseAvailabilityQueryInput.groupSize),
      placeholder: t`Group Size`,
    },
    {
      value: holeCountDisplay(courseAvailabilityQueryInput.holeCount),
      placeholder: t`Holes`,
    },
    {
      value: timeOfDayDisplay(
        courseAvailabilityQueryInput.timeRange ?? null,
        departmentStartEndTime?.startTime,
        departmentStartEndTime?.endTime
      ),
      placeholder: t`Start Time`,
    },
  ];

  return agendaFilterBarValues;
};

const timeOfDayDisplay = (
  timeOfDay: { timeGTE?: string; timeLTE?: string } | null,
  departmentStartTime?: string,
  departmentEndTime?: string
): string | null => {
  if (timeOfDay === null) {
    return null;
  }

  const formattedStartTime = formatSliderTimeLabel(
    timeOfDay.timeGTE || departmentStartTime || ""
  );
  const formattedEndTime = formatSliderTimeLabel(
    timeOfDay.timeLTE || departmentEndTime || ""
  );

  return formattedStartTime && formattedEndTime
    ? `${formattedStartTime} - ${formattedEndTime}`
    : null;
};

const groupSizeDisplay = (groupSize?: number): string | null => {
  if (isNil(groupSize)) {
    return null;
  }
  return groupSize === 1 ? "1 person" : `${groupSize} people`;
};

const holeCountDisplay = (holeCount?: HoleCount): string | null => {
  if (isNil(holeCount) || holeCount === "NONE") {
    return null;
  }
  return holeCount === "EIGHTEEN" ? "18 holes" : "9 holes";
};

const courseDisplay = (
  selectedCourseIds: string[],
  courseNoun: { singular: string; plural: string }
): string | null => {
  if (selectedCourseIds.length === 0) {
    return null;
  }
  return selectedCourseIds.length === 1
    ? `1 ${courseNoun.singular}`
    : `${selectedCourseIds.length} ${courseNoun.plural}`;
};

const useGetDepartmentAvailabilityFilterBarValuesFragmentString = graphql`
  fragment useGetDepartmentAvailabilityFilterBarValues_department on Department {
    ...useDepartmentGolfFacilitiesStartEndTime_department
    facilities {
      edges {
        node {
          type
          copyOverrides {
            course {
              singular
              plural
            }
          }
        }
      }
    }
  }
`;
